
















































import { Component, Prop, Vue } from 'vue-property-decorator';
import { Modal } from 'ant-design-vue';

export interface IUploadStatus {
  status: string;
  progress?: number;
}

@Component({})
export default class UploadWithErrorsBlock extends Vue {
  @Prop({ type: Number, default: 0 }) readonly errorsAmount!: number;
  @Prop({ type: Boolean, default: true }) uploadButtonsDisabled!: boolean;
  @Prop({ type: Boolean, default: false }) fileInputDisabled!: boolean;
  @Prop({ type: String, default: undefined }) readonly buttonLabelPartial?: string;
  @Prop({ type: String, default: undefined }) readonly buttonLabelFull?: string;
  @Prop({ type: String, default: undefined }) readonly errorsTooltipTitle?: string;
  @Prop({ type: String, default: undefined }) readonly filePlaceholder?: string;
  @Prop({ type: String, default: undefined }) readonly uploadModalTitle?: string;
  @Prop({ type: String, default: undefined }) readonly uploadModalMessage?: string;
  @Prop({ type: String, default: undefined }) readonly confirmFullTitle?: string;
  @Prop({ type: String, default: undefined }) readonly confirmFullContent?: string;

  get effectiveButtonLabelPartial(): string {
    return this.buttonLabelPartial != null && this.buttonLabelPartial !== '' ? this.buttonLabelPartial : this.$tc('shared.actions.update');
  }
  get effectiveButtonLabelFull(): string {
    return this.buttonLabelFull != null && this.buttonLabelFull !== '' ? this.buttonLabelFull : this.$tc('shared.uploadWithErrors.updateAll');
  }
  get effectiveErrorsTooltipTitle(): string {
    return this.errorsTooltipTitle != null && this.errorsTooltipTitle !== '' ? this.errorsTooltipTitle : this.$tc('shared.uploadWithErrors.errors');
  }
  get effectiveFilePlaceholder(): string {
    return this.filePlaceholder != null && this.filePlaceholder !== '' ? this.filePlaceholder : this.$tc('shared.messages.chooseFilePlaceholder');
  }
  get effectiveUploadModalTitle(): string {
    return this.uploadModalTitle != null && this.uploadModalTitle !== '' ? this.uploadModalTitle : this.$tc('shared.uploadWithErrors.updating');
  }
  get effectiveUploadModalMessage(): string {
    return this.uploadModalMessage != null && this.uploadModalMessage !== '' ? this.uploadModalMessage : this.$tc('shared.uploadWithErrors.pleaseWaitUpdating');
  }
  get effectiveConfirmFullTitle(): string {
    return this.confirmFullTitle != null && this.confirmFullTitle !== '' ? this.confirmFullTitle : this.$tc('shared.uploadWithErrors.updateFully');
  }
  get effectiveConfirmFullContent(): string {
    return this.confirmFullContent != null && this.confirmFullContent !== '' ? this.confirmFullContent : this.$tc('shared.uploadWithErrors.confirmFullUpdateContent');
  }

  /** Вызывается при клике «Скачать ошибки». Родитель сам делает запрос и скачивание. */
  @Prop({ type: Function, required: true }) readonly onDownloadErrors!: () => void;

  /** Вызывается при клике по кнопке частичного обновления. Передаётся файл. Должен вернуть Promise. При resolve() компонент запускает опрос статуса. */
  @Prop({ type: Function, required: true }) readonly onUploadPartial!: (file: File) => Promise<void>;

  /** Вызывается при подтверждении полного обновления. Если не передан — вторая кнопка не отображается. */
  @Prop({ type: Function, default: undefined }) readonly onUploadFull?: (file: File) => Promise<void>;

  /** Опрос статуса загрузки. Вызывается периодически до COMPLETED/FAILED. */
  @Prop({ type: Function, required: true }) readonly getUploadStatus!: () => Promise<IUploadStatus>;

  /** Вызывается по завершении загрузки (успех или ошибка). Родитель может обновить список и счётчик ошибок. */
  @Prop({ type: Function, default: () => {} }) readonly onUploadComplete!: () => void;

  /** Вызывается при статусе COMPLETED (тост успеха — например showInfo). */
  @Prop({ type: Function, default: undefined }) readonly onUploadSuccess?: () => void;

  /** Вызывается при статусе FAILED (тост ошибки — например showError). */
  @Prop({ type: Function, default: undefined }) readonly onUploadError?: () => void;

  uploadedFiles: File | null = null;
  uploadModalVisible = false;
  uploadProgress = 0;
  private uploadStatusPollInterval: number = 0;
  private isCheckingUploadStatus = false;

  /** Кнопки загрузки неактивны: нет файла, идёт загрузка или родитель отключил (например, нет рынка). */
  get effectiveUploadButtonsDisabled(): boolean {
    return this.uploadButtonsDisabled || !this.uploadedFiles || this.uploadModalVisible;
  }

  /** Инпут файла неактивен: идёт загрузка или родитель отключил (например, загрузка таблицы). */
  get effectiveFileInputDisabled(): boolean {
    return this.fileInputDisabled || this.uploadModalVisible;
  }

  get badgeStyle(): Record<string, string> {
    const condition = !!this.errorsAmount;
    return {
      backgroundColor: 'white',
      size: '10px',
      color: condition ? 'red' : 'gray',
      borderColor: condition ? 'red' : 'gray',
    };
  }

  showFullUpdateConfirm(): void {
    if (!this.onUploadFull) return;
    const uploadFull = this.onUploadFull;
    Modal.confirm({
      title: this.effectiveConfirmFullTitle,
      content: this.effectiveConfirmFullContent,
      centered: true,
      okText: this.$tc('shared.actions.yes'),
      cancelText: this.$tc('shared.actions.no'),
      onOk: () => this.runUpload(uploadFull),
    });
  }

  handlePartialClick(): void {
    this.runUpload(this.onUploadPartial);
  }

  private runUpload(uploadFn: (file: File) => Promise<void>): void {
    if (!this.uploadedFiles) return;

    const file = this.uploadedFiles;
    this.uploadModalVisible = true;
    this.uploadProgress = 0;

    uploadFn(file)
      .then(() => {
        this.uploadedFiles = null;
        this.startPolling();
      })
      .catch(() => {
        this.uploadModalVisible = false;
        this.onUploadComplete();
      });
  }

  private startPolling(): void {
    this.stopPolling();
    this.uploadStatusPollInterval = window.setInterval(() => this.checkStatus(), 5000);
    this.checkStatus();
  }

  private stopPolling(): void {
    if (this.uploadStatusPollInterval) {
      window.clearInterval(this.uploadStatusPollInterval);
      this.uploadStatusPollInterval = 0;
    }
  }

  private finishPolling(): void {
    this.stopPolling();
    this.uploadModalVisible = false;
    this.onUploadComplete();
  }

  private checkStatus(): void {
    if (this.isCheckingUploadStatus) return;
    this.isCheckingUploadStatus = true;
    this.getUploadStatus()
      .then((task) => {
        if (task.progress != null) {
          this.uploadProgress = task.progress;
        }
        if (task.status === 'COMPLETED') {
          this.onUploadSuccess && this.onUploadSuccess();
          this.finishPolling();
        } else if (task.status === 'FAILED') {
          this.onUploadError && this.onUploadError();
          this.finishPolling();
        }
      })
      .catch(() => {
        this.finishPolling();
      })
      .finally(() => {
        this.isCheckingUploadStatus = false;
      });
  }

  beforeDestroy(): void {
    this.stopPolling();
  }
}
